import "./Licensing.css";

const licenseTypes = [
  {
    label: "Personal",
    title: "Personal Digital Download",
    bestFor: "Home decor, personal printing, personal devices, gifts, and personal social media.",
    allowed: [
      "Print for your own home, office, classroom, or private display.",
      "Use on personal devices or personal creative projects.",
      "Keep one backup copy for your own records.",
    ],
    notFor: "Business use, resale, redistribution, paid client work, or commercial campaigns.",
  },
  {
    label: "Commercial",
    title: "Commercial License",
    bestFor: "Business websites, blogs, social media graphics, advertising, presentations, and client projects.",
    allowed: [
      "Use in business websites, marketing visuals, blogs, and social media graphics.",
      "Use inside client presentations or larger design projects.",
      "Crop, resize, and edit for the permitted project.",
    ],
    notFor: "Reselling the original file, uploading to stock sites, or selling as standalone wall art.",
  },
  {
    label: "Extended",
    title: "Extended Commercial License",
    bestFor: "Larger campaigns, packaging, templates, products for sale, or higher-volume business use.",
    allowed: [
      "Use in wider commercial projects when approved by product page, invoice, or written agreement.",
      "Use in larger marketing campaigns or higher-volume design use.",
      "Request custom terms for special usage.",
    ],
    notFor: "Claiming copyright, trademark use, or standalone redistribution of the original image.",
  },
  {
    label: "Editorial",
    title: "Editorial / Fine Art Use",
    bestFor: "Documentary, cultural, educational, news, blog, portfolio, classroom, and fine-art display use.",
    allowed: [
      "Use in editorial articles, educational content, cultural storytelling, or documentary projects.",
      "Use for fine-art display and portfolio-style visual presentation.",
      "Use carefully when people, property, signs, or trademarks appear.",
    ],
    notFor: "Advertising or endorsement use without required model/property release permission.",
  },
];

const quickAnswers = [
  {
    q: "Can I print it for my home?",
    a: "Yes. Choose a Personal Digital Download license for personal wall art and home decor printing.",
  },
  {
    q: "Can I use it on my business website?",
    a: "Yes. Choose a Commercial License for business websites, blogs, marketing, and brand visuals.",
  },
  {
    q: "Can I sell it as my own poster?",
    a: "No. Selling the original or lightly modified image as standalone art is not allowed without separate written permission.",
  },
  {
    q: "Can I use it in a client design?",
    a: "Yes, with a Commercial License if the image is part of a larger design or project.",
  },
  {
    q: "Can I use a photo with people in advertising?",
    a: "Only if the product includes the necessary release or you obtain the required permission.",
  },
  {
    q: "Can I use files for AI training?",
    a: "No. AI training, datasets, scraping libraries, and machine-learning asset collections require separate written permission.",
  },
];

const allowedUses = [
  "Print for personal home decor when your license allows it.",
  "Use in websites, blogs, social media, or presentations with the correct commercial license.",
  "Crop, resize, or edit the image for a permitted project.",
  "Use for editorial, educational, documentary, or fine-art purposes where appropriate.",
  "Keep a backup copy for your own records.",
];

const notAllowedUses = [
  "Do not resell, share, gift, sublicense, upload, or redistribute the original file.",
  "Do not sell the image as a standalone poster, wall art print, stock asset, template asset, or digital download.",
  "Do not upload the file to stock websites, free download sites, design marketplaces, or shared drives.",
  "Do not claim the image as your own work or register it as your copyright.",
  "Do not use the file in logos, trademarks, AI training, datasets, or scraping collections without written permission.",
];

const downloadItems = [
  "High-resolution JPG, PNG, TIFF, WEBP, PDF, or ZIP files depending on the product.",
  "License terms PDF for your records.",
  "Usage guide PDF with printing and file-handling tips.",
  "Thank-you note and support information when included.",
  "Printable size guidance when included with MayaChitro products.",
];

const printTips = [
  "Use high-quality photo paper, fine-art paper, matte, luster, or satin finish for elegant display.",
  "Use a trusted print shop for premium wall art results.",
  "Choose the correct file ratio or paper size before printing.",
  "Avoid fit-to-page settings if you want the exact crop.",
  "Print a small test first for important projects.",
  "Let inkjet prints dry fully before framing.",
];

function Licensing() {
  return (
    <main className="mc-license-page">
      <section
        className="mc-license-hero"
        style={{ "--license-hero-image": 'url("/images/golden-hour-canoe-river-reflection-dramatic-clouds-mayachitro.jpg")' }}
      >
        <div className="mc-license-hero-copy">
          <p className="mc-license-kicker">MayaChitro Licensing</p>
          <h1>Beautiful imagery.<br /><em>Clear possibilities.</em></h1>
          <p>
            Choose the right license for personal decor, editorial storytelling, business websites,
            marketing projects, client work, or larger commercial use.
          </p>

          <div className="mc-license-actions">
            <a href="#license-types" className="mc-license-btn primary">
              Choose a License
            </a>
            <a href="/contact" className="mc-license-btn secondary">
              Commercial Use Request
            </a>
          </div>
        </div>

        <div className="mc-license-hero-card">
          <span>The simple promise</span>
          <h2>You buy usage rights, not copyright ownership.</h2>
          <p>
            MayaChitro retains ownership of the original photographs,
            artwork, previews, mockups, source designs, and related creative assets.
          </p>
        </div>
      </section>

      <section className="mc-license-trustbar" aria-label="Licensing highlights">
        <div>
          <b>01</b><strong>Personal Use</strong>
          <span>Home printing & decor</span>
        </div>
        <div>
          <b>02</b><strong>Commercial Use</strong>
          <span>Websites, blogs & marketing</span>
        </div>
        <div>
          <b>03</b><strong>Editorial Use</strong>
          <span>Storytelling & education</span>
        </div>
        <div>
          <b>04</b><strong>Secure Downloads</strong>
          <span>Token-based file access</span>
        </div>
      </section>

      <section className="mc-license-selector">
        <div className="mc-license-section-head">
          <p className="mc-license-kicker">Quick Selector</p>
          <h2>Which license do you need?</h2>
          <p>
            Start with the purpose of your project. Personal display, business promotion,
            editorial storytelling, and larger commercial use require different rights.
          </p>
        </div>

        <div className="mc-license-selector-grid">
          <a href="#personal-license">
            <span>For my home</span>
            <strong>Personal License</strong><i>→</i>
          </a>
          <a href="#commercial-license">
            <span>For my business</span>
            <strong>Commercial License</strong><i>→</i>
          </a>
          <a href="#extended-license">
            <span>For larger use</span>
            <strong>Extended License</strong><i>→</i>
          </a>
          <a href="#editorial-license">
            <span>For articles or education</span>
            <strong>Editorial / Fine Art</strong><i>→</i>
          </a>
        </div>
      </section>

      <section className="mc-license-section" id="license-types">
        <div className="mc-license-section-head">
          <p className="mc-license-kicker">License Types</p>
          <h2>Simple options for different buyer needs.</h2>
        </div>

        <div className="mc-license-card-grid">
          {licenseTypes.map((item, index) => {
            const sectionId =
              index === 0
                ? "personal-license"
                : index === 1
                ? "commercial-license"
                : index === 2
                ? "extended-license"
                : "editorial-license";

            return (
              <article className={`mc-license-card ${index === 1 ? "featured" : ""}`} id={sectionId} key={item.title}>
                {index === 1 && <b className="mc-license-popular">Most versatile</b>}
                <span>{item.label}</span>
                <h3>{item.title}</h3>
                <p>{item.bestFor}</p>

                <h4>Allowed</h4>
                <ul>
                  {item.allowed.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                <div className="mc-license-limit">
                  <strong>Main limit:</strong> {item.notFor}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mc-license-split">
        <div className="mc-license-panel positive">
          <p className="mc-license-kicker">Allowed Uses</p>
          <h2>What you may do</h2>
          <ul>
            {allowedUses.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="mc-license-panel warning">
          <p className="mc-license-kicker">Important Limits</p>
          <h2>What you may not do</h2>
          <ul>
            {notAllowedUses.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mc-license-release-note">
        <div>
          <p className="mc-license-kicker">People, Property & Editorial Use</p>
          <h2>Use extra care when people, private property, signage, or trademarks appear.</h2>
        </div>
        <p>
          Some photographs may include identifiable people, buildings, vehicles, signs, brand marks,
          or privately owned property. Unless a product page specifically confirms a model release
          or property release, treat those images as editorial, documentary, cultural, educational,
          or fine-art use only. For advertising, endorsement, sensitive topics, or commercial claims,
          the buyer is responsible for obtaining any additional permission required.
        </p>
      </section>

      <section className="mc-license-section">
        <div className="mc-license-section-head">
          <p className="mc-license-kicker">Download Package</p>
          <h2>What your download may include</h2>
          <p>
            Product contents vary by listing, but MayaChitro downloads may
            include artwork files, license documents, usage guidance, and support notes.
          </p>
        </div>

        <div className="mc-license-check-grid">
          {downloadItems.map((item) => (
            <div className="mc-license-check" key={item}>
              <span>✓</span>
              <p>{item}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mc-license-section">
        <div className="mc-license-section-head">
          <p className="mc-license-kicker">Print Guide</p>
          <h2>Printing and display tips</h2>
          <p>
            Use the correct file size, print ratio, and paper type to get a cleaner, more premium
            final result.
          </p>
        </div>

        <div className="mc-license-print-box">
          {printTips.map((tip) => (
            <div key={tip}>
              <span>•</span>
              <p>{tip}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mc-license-section">
        <div className="mc-license-section-head">
          <p className="mc-license-kicker">Quick Answers</p>
          <h2>Can I use this?</h2>
        </div>

        <div className="mc-license-faq-grid">
          {quickAnswers.map((item) => (
            <details className="mc-license-faq" key={item.q}>
              <summary><h3>{item.q}</h3><span>+</span></summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mc-license-cta">
        <div>
          <p className="mc-license-kicker">Need a custom license?</p>
          <h2>For campaign, bulk, publication, or special commercial use, contact MayaChitro.</h2>
          <p>
            Share the image name, product link, intended use, territory, duration, and distribution
            details so the right license can be reviewed.
          </p>
        </div>

        <div className="mc-license-actions">
          <a href="/contact" className="mc-license-btn primary">
            Contact for Licensing
          </a>
          <a href="/shop" className="mc-license-btn secondary">
            Browse Downloads
          </a>
        </div>
      </section>
    </main>
  );
}

export default Licensing;
